import { useMemo, useState } from 'react'
import { Plus, Puzzle } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useListCatalogComponents } from '@/shared/hooks/api/catalog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { V2CatalogListEmpty } from '../components/V2CatalogListEmpty'
import { V2CatalogPageHeader } from '../components/V2CatalogPageHeader'
import { V2CatalogTableFrame } from '../components/V2CatalogTableFrame'
import { V2TableLink } from '../components/V2TableLink'
import { V2CreateComponentDialog } from '../components/V2CreateComponentDialog'
import { V2ComponentRohsProgressBadge } from '../components/component-rohs-briefing/V2ComponentRohsProgressBadge'
import { getV2ComponentDetailPath } from '../utils/routes'

const COMPONENT_TABLE_COLUMNS = [
  { width: '26%' },
  { width: '18%' },
  { width: '26%' },
  { width: '14%' },
  { width: '16%' },
] as const

function ComponentsTableColGroup() {
  return (
    <colgroup>
      {COMPONENT_TABLE_COLUMNS.map((column, index) => (
        <col key={index} style={{ width: column.width }} />
      ))}
    </colgroup>
  )
}

export default function V2ComponentsPage() {
  const { t } = useTranslation('common')
  const { data: components = [], isSuccess, isLoading, isError } =
    useListCatalogComponents()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [hideUnlinked, setHideUnlinked] = useState(false)

  const visibleComponents = useMemo(() => {
    if (!hideUnlinked) {
      return components
    }
    return components.filter((component) => component.linkedProductCount > 0)
  }, [components, hideUnlinked])

  const hasComponents = isSuccess && components.length > 0
  const showEmptyState = !isLoading && isSuccess && components.length === 0
  const showFilteredEmpty =
    !isLoading &&
    isSuccess &&
    hasComponents &&
    hideUnlinked &&
    visibleComponents.length === 0

  return (
    <>
      <div className="space-y-4">
        <V2CatalogPageHeader
          title={t('v2.pages.components.title')}
          subtitle={t('v2.pages.components.subtitle')}
          actions={
            <Button
              className="gap-2"
              onClick={() => setCreateDialogOpen(true)}
              type="button"
            >
              <Plus className="size-4" />
              {t('v2.pages.components.addButton')}
            </Button>
          }
        />

        {hasComponents && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={hideUnlinked}
              id="hide-unlinked-components"
              onCheckedChange={(checked) => {
                setHideUnlinked(checked === true)
              }}
            />
            <Label htmlFor="hide-unlinked-components" className="font-normal">
              {t('v2.pages.components.filter.hideUnlinked')}
            </Label>
          </div>
        )}

        {(isLoading || hasComponents) && (
          <V2CatalogTableFrame
            isLoading={isLoading}
            skeletonColumnCount={COMPONENT_TABLE_COLUMNS.length}
          >
            {hasComponents && visibleComponents.length > 0 ? (
              <table className="w-full min-w-full table-fixed caption-bottom text-sm">
                <ComponentsTableColGroup />
                <TableHeader>
                  <TableRow className="group/row">
                    <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                      {t('v2.pages.components.columns.name')}
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                      {t('v2.pages.components.columns.sku')}
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                      {t('v2.pages.components.columns.supplier')}
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-background group-hover/row:bg-muted">
                      {t('v2.pages.components.columns.rohs')}
                    </TableHead>
                    <TableHead className="sticky top-0 z-10 bg-background text-end group-hover/row:bg-muted">
                      {t('v2.pages.components.columns.products')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleComponents.map((component) => {
                    const nameLabel =
                      component.name != null && component.name.trim() !== ''
                        ? component.name.trim()
                        : t('v2.pages.components.fallbackName')

                    return (
                      <TableRow key={component.id} className="group/row">
                        <TableCell className="max-w-0 truncate overflow-hidden bg-background group-hover/row:bg-muted">
                          <V2TableLink
                            truncate
                            title={nameLabel}
                            to={getV2ComponentDetailPath(component.id)}
                          >
                            {nameLabel}
                          </V2TableLink>
                        </TableCell>
                        <TableCell className="max-w-0 truncate overflow-hidden bg-background text-muted-foreground group-hover/row:bg-muted">
                          <V2TableLink
                            truncate
                            title={component.sku}
                            to={getV2ComponentDetailPath(component.id)}
                          >
                            {component.sku}
                          </V2TableLink>
                        </TableCell>
                        <TableCell className="max-w-0 truncate overflow-hidden bg-background group-hover/row:bg-muted">
                          <span
                            className="block truncate"
                            title={component.supplier.name}
                          >
                            {component.supplier.name}
                          </span>
                        </TableCell>
                        <TableCell className="bg-background group-hover/row:bg-muted">
                          {component.rohsFile == null ? (
                            <span className="text-muted-foreground">
                              {t('notAvailable')}
                            </span>
                          ) : (
                            <V2ComponentRohsProgressBadge
                              rohsFile={component.rohsFile}
                            />
                          )}
                        </TableCell>
                        <TableCell className="bg-background text-end tabular-nums text-muted-foreground group-hover/row:bg-muted">
                          {component.linkedProductCount}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </table>
            ) : null}
          </V2CatalogTableFrame>
        )}

        {showFilteredEmpty && (
          <p className="text-sm text-muted-foreground">
            {t('v2.pages.components.filter.noLinkedParts')}
          </p>
        )}

        {showEmptyState && (
          <V2CatalogListEmpty
            icon={<Puzzle aria-hidden />}
            title={t('v2.pages.components.empty.title')}
            description={t('v2.pages.components.empty.description')}
            action={
              <Button
                className="gap-2"
                onClick={() => setCreateDialogOpen(true)}
                type="button"
              >
                <Plus className="size-4" />
                {t('v2.pages.components.empty.addButton')}
              </Button>
            }
          />
        )}

        {isError && (
          <p className="text-sm text-destructive">
            {t('notifications.unknownError')}
          </p>
        )}
      </div>

      <V2CreateComponentDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />
    </>
  )
}
